import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

interface Props {
  children: ReactNode;
  /** Optional name for the chart to show in error message */
  chartName?: string;
  /** Optional custom fallback UI */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary specifically for chart components.
 * Catches rendering errors in child charts and displays a friendly fallback
 * instead of crashing the entire page.
 *
 * Usage:
 * ```tsx
 * <ChartErrorBoundary chartName="Trend Chart">
 *   <TrendChart data={data} />
 * </ChartErrorBoundary>
 * ```
 */
class ChartErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error for debugging
    console.error(`ChartErrorBoundary caught error in ${this.props.chartName || 'chart'}:`, error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center p-6 bg-ui-background-secondary rounded-lg border border-ui-border-primary min-h-[200px]">
          <AlertTriangle className="w-8 h-8 text-amber-500 mb-2" />
          <p className="text-sm text-ui-text-secondary text-center">
            {this.props.chartName
              ? `Unable to render ${this.props.chartName}`
              : 'Unable to render chart'}
          </p>
          <p className="text-xs text-ui-text-tertiary mt-1">
            Try refreshing the page
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChartErrorBoundary;
